import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { Toaster } from "sonner";
// Side-effect import: patches sonner's `toast.success/info/warning/error`
// defaults sitewide (duration + closeButton). Must stay above any other
// module that calls `toast.*` at import time.
import "./lib/toastConfig";

import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import ProfilePage from "@/pages/ProfilePage";
import PublicProfilePage from "@/pages/PublicProfilePage";
import SettingsPage from "@/pages/SettingsPage";
import JobsList from "@/pages/JobsList";
import JobNew from "@/pages/JobNew";
import JobDetail from "@/pages/JobDetail";
import MyApplications from "@/pages/MyApplications";
import Messages from "@/pages/Messages";
import ReviewsPage from "@/pages/Reviews";
import CoffeeChatsPage from "@/pages/CoffeeChats";
import CommunityPage from "@/pages/Community";
import NotificationsPage from "@/pages/NotificationsPage";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import SearchPage from "@/pages/Search";
import FavoritesPage from "@/pages/Favorites";
import { HowItWorks, Handbook, Blog } from "@/pages/StaticPages";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import BetaTerms from "@/pages/legal/BetaTerms";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import CommunityGuidelines from "@/pages/legal/CommunityGuidelines";
import PaymentGuidance from "@/pages/legal/PaymentGuidance";
import EarlyAccessAgreementPolicy from "@/pages/legal/EarlyAccessAgreementPolicy";
import SupportContact from "@/pages/SupportContact";
import Unsubscribe from "@/pages/Unsubscribe";
import FAQ from "@/pages/FAQ";
import NotFound from "@/pages/NotFound";
import AdminFeedback from "@/pages/AdminFeedback";
import MyFeedback from "@/pages/MyFeedback";
import AdminUsers from "@/pages/AdminUsers";
import AdminEmailCenter from "@/pages/AdminEmailCenter";
import AdminCertifications from "@/pages/AdminCertifications";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminAcknowledgments from "@/pages/AdminAcknowledgments";
import AdminCommunityUpdates from "@/pages/AdminCommunityUpdates";
import AdminPrivateTesterRequests from "@/pages/AdminPrivateTesterRequests";
import WhatsNewModal from "@/components/WhatsNewModal";
import EmailConsentBanner from "@/components/EmailConsentBanner";
import BetaAcknowledgmentModal from "@/components/BetaAcknowledgmentModal";
import TermsAcceptanceGate from "@/components/TermsAcceptanceGate";
import { FeedbackButton } from "@/components/feedback/FeedbackButton";

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/register" element={<AuthPage mode="register" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/handbook" element={<Handbook />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/beta-terms" element={<BetaTerms />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/early-access-agreement" element={<EarlyAccessAgreementPolicy />} />
            <Route path="/payment-guidance" element={<PaymentGuidance />} />
            <Route path="/contact" element={<SupportContact />} />
            <Route path="/support" element={<SupportContact />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/profile/:userId" element={<ProtectedRoute><PublicProfilePage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

            <Route path="/jobs" element={<ProtectedRoute><JobsList /></ProtectedRoute>} />
            <Route path="/jobs/new" element={<ProtectedRoute><JobNew /></ProtectedRoute>} />
            <Route path="/jobs/:id/edit" element={<ProtectedRoute><JobNew /></ProtectedRoute>} />
            <Route path="/jobs/:id" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
            <Route path="/applications" element={<ProtectedRoute><MyApplications /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/messages/:threadId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/reviews" element={<ProtectedRoute><ReviewsPage /></ProtectedRoute>} />
            <Route path="/coffee-chats" element={<ProtectedRoute><CoffeeChatsPage /></ProtectedRoute>} />
            <Route path="/community" element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />

            <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
            <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />

            <Route path="/admin/feedback" element={<ProtectedRoute><AdminFeedback /></ProtectedRoute>} />
            <Route path="/my-feedback" element={<ProtectedRoute><MyFeedback /></ProtectedRoute>} />
            <Route path="/my-feedback/:id" element={<ProtectedRoute><MyFeedback /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/email-center" element={<ProtectedRoute><AdminEmailCenter /></ProtectedRoute>} />
            <Route path="/admin/certifications" element={<ProtectedRoute><AdminCertifications /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
            <Route path="/admin/acknowledgments" element={<ProtectedRoute><AdminAcknowledgments /></ProtectedRoute>} />
            <Route path="/admin/community-updates" element={<ProtectedRoute><AdminCommunityUpdates /></ProtectedRoute>} />
            <Route path="/admin/private-tester-requests" element={<ProtectedRoute><AdminPrivateTesterRequests /></ProtectedRoute>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          <FeedbackButton />
          <TermsAcceptanceGate />
          <BetaAcknowledgmentModal />
          <EmailConsentBanner />
          <WhatsNewModal />
          <AppToaster />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function AppToaster() {
  const { pathname } = useLocation();
  const top = useHeaderBottom(pathname);
  return (
    <Toaster
      closeButton
      position="top-center"
      offset={{ top }}
      mobileOffset={{ top, left: 12, right: 12 }}
      visibleToasts={3}
      toastOptions={{
        style: {
          maxWidth: "min(92vw, 420px)",
          whiteSpace: "normal",
          wordBreak: "break-word",
        },
        classNames: { toast: "font-sans lb-toast" },
      }}
    />
  );
}

// Tracks the live bottom edge of the site header so toasts always sit just below it.
function useHeaderBottom(pathname) {
  const [top, setTop] = useState(16);
  useEffect(() => {
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const header = document.querySelector("header.glass-nav");
        const bottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        setTop(Math.round(bottom) + 12);
      });
    };
    measure();
    const header = document.querySelector("header.glass-nav");
    const ro = header && typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(header);
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    const late = setTimeout(measure, 400);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(late);
      ro?.disconnect();
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [pathname]);
  return top;
}

export default App;
